import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"

import {
  DiscountStatus,
  DiscountType,
} from "pages/discount/src/discount-modals/common"
import { defaultAxiosInstance } from "services/client"
import { ApiStatus, ResponseData } from "services/common"

export const defaultUrl = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/templates/discount`

export interface BaseDiscountItem {
  discount_id: number | string | null
  discount_type: DiscountType
  discount_percent: string
  effective_from?: string
  effective_to?: string
}

// For TIERED, EXTENDED_STAY and EARLY_BIRD
export interface StayBasedDiscountItem extends BaseDiscountItem {
  min_stay: string
  max_stay: string | null
}

// For DAY_BASED
export interface DayBasedDiscountItem extends BaseDiscountItem {
  day: number
}

// For SEASONAL_BASED
export interface SeasonalBasedDiscountItem extends BaseDiscountItem {
  name: string
}

// NEW LISTING: first N bookings
export interface NewListingDiscountItem extends BaseDiscountItem {
  number_of_bookings?: number
}

// For DYNAMIC_DISCOUNT
export interface DynamicDiscountItem extends BaseDiscountItem {
  min_discount: string
  max_discount: string
}

export type TieredDiscountItem =
  | BaseDiscountItem
  | StayBasedDiscountItem
  | DayBasedDiscountItem
  | SeasonalBasedDiscountItem
  | NewListingDiscountItem
  | DynamicDiscountItem

export interface DiscountTemplateTypeRequest {
  id?: number | null
  pmcId: string | undefined
  discountTemplateName: string
  description?: string
  lastMinuteDays: number
  discountType: DiscountType
  status: DiscountStatus
  tieredDiscountList: TieredDiscountItem[]
  isDefault: boolean
  isRepeatEveryYear?: boolean
  isAdminDiscount?: boolean
}

export interface DiscountTemplateType {
  id: number | undefined
  pmcId: string
  discountTemplateName: string
  discountPercent: number
  regularDiscountPercent?: number
  lastMinuteDays?: number
  active?: boolean
  tiered: boolean
  tieredDiscountList: DiscountType[]
  isDefault: boolean
}

export interface CreateDiscountTemplateData extends ResponseData {
  data: DiscountTemplateType
}

export interface CreateDiscountTemplateResponse extends AxiosResponse {
  data: CreateDiscountTemplateData
}

export function createOrUpdateDiscount(
  data: DiscountTemplateTypeRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<CreateDiscountTemplateResponse> {
  return axiosInstance({
    method: data?.id ? "put" : "post",
    data,
    url: defaultUrl,
  }) as Promise<CreateDiscountTemplateResponse>
}

// ------------ get discount Apis-------------

export interface TieredDiscountType {
  discount_id: string | null
  effective_from: number | null
  effective_to: number | null
  min_stay: number | null
  max_stay: number | null
  discount_percent: number | null
  is_last_minute: boolean | null
  days_to_arrival_from: number | null
  days_to_arrival_to: number | null
  is_whimstay_discount: boolean | null
  is_host_discount: boolean | null
  additional_discount: number | null
  whimstay_incentive: number | null
  report_id: string | null
  name: string | null
  discount_type: string | null
  discount_template_id: string | null
  day: number | null
  number_of_bookings: number | null
  expires_on: number | null
  newListingUsedCount?: number | null
  min_discount: number | null
  max_discount: number | null
}

export interface DiscountTemplateDataType {
  id: number
  active: boolean
  description: string
  discountTemplateName: string
  activePropertyCount: number
  lastMinuteDays: number
  pmcId: string
  tieredDiscountList: TieredDiscountType[]
  isDefault: boolean
  status: string
  discountType: string
  isRepeatEveryYear: boolean
}

export interface FetchDiscountTemplatesByPmcIdData {
  content: DiscountTemplateDataType[]
  pageable: {
    sort: {
      empty: boolean
      sorted: boolean
      unsorted: boolean
    }
    offset: number
    pageNumber: number
    pageSize: number
    paged: boolean
    unpaged: boolean
  }
  last: boolean
  totalPages: number
  totalElements: number
  number: number
  sort: {
    empty: boolean
    sorted: boolean
    unsorted: boolean
  }
  size: number
  first: boolean
  numberOfElements: number
  empty: boolean
}

export interface FetchDiscountTemplatesByPmcIdResponseData
  extends AxiosResponse {
  data: FetchDiscountTemplatesByPmcIdData
}
export interface FetchDiscountTemplatesByPmcIdResponse extends AxiosResponse {
  data: FetchDiscountTemplatesByPmcIdResponseData
}

export interface FetchDiscountTemplatesByPmcIdRequestData {
  pmcId: string
  discountType: string
  status: string
  keyword: string
  pageNo: number
  noOfRecords: number
  sortDirection: string
  sortBy?: string
  propertyId?: string
  isAdminDiscount?: boolean
}

export interface FetchDiscountTemplatesByPmcIdRequest
  extends Partial<AxiosRequestConfig> {
  params: FetchDiscountTemplatesByPmcIdRequestData
}

export function fetchDiscountTemplatesByPmcId(
  {
    params,
    method = "get",
    url = defaultUrl,
    ...config
  }: FetchDiscountTemplatesByPmcIdRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<FetchDiscountTemplatesByPmcIdResponse> {
  return axiosInstance({
    params,
    method,
    url,
    ...config,
  }) as Promise<FetchDiscountTemplatesByPmcIdResponse>
}

// ------------ get discount by properties id ----------

export interface GetDiscountTemplatesByPropertyIdResponseData
  extends AxiosResponse {
  data: DiscountTemplateDataType[]
}
export interface GetDiscountTemplatesByPropertyIdResponse
  extends AxiosResponse {
  data: GetDiscountTemplatesByPropertyIdResponseData
}

export interface GetDiscountTemplatesByPropertyIdRequestData {
  pmcId: string
  propertyId: string
}

export interface GetDiscountTemplatesByPropertyIdRequest
  extends Partial<AxiosRequestConfig> {
  params: GetDiscountTemplatesByPropertyIdRequestData
}

export function getDiscountTemplatesByPropertyId(
  {
    params,
    method = "get",
    url = `${defaultUrl}/by-property`,
    ...config
  }: GetDiscountTemplatesByPropertyIdRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<GetDiscountTemplatesByPropertyIdResponse> {
  return axiosInstance({
    params,
    method,
    url,
    ...config,
  }) as Promise<GetDiscountTemplatesByPropertyIdResponse>
}

// -----------------delete discount Apis-------

export interface DiscountTemplateDeleteResponseData extends ResponseData {
  data: unknown
}

export interface DiscountTemplateDeleteResponse extends AxiosResponse {
  data: DiscountTemplateDeleteResponseData
}

export interface DeleteDiscountTemplateRequestType {
  id: number
  pmcId: string
}

export function deleteDiscountApi(
  data: DeleteDiscountTemplateRequestType,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<DiscountTemplateDeleteResponse> {
  return axiosInstance({
    method: "delete",
    params: { templateId: data?.id, pmcId: data.pmcId },
    url: defaultUrl,
  }) as Promise<DiscountTemplateDeleteResponse>
}

// --------------- Apply discount api - your properties -------------
export interface ApplyDiscountByPropertyResponseData extends ResponseData {
  data: unknown
}

export interface ApplyDiscountByPropertyResponse extends AxiosResponse {
  data: ApplyDiscountByPropertyResponseData
}

export type ApplyDiscountByPropertyRequestData = {
  pmcId: string
  propertyDetails: {
    propertyId: string
    discountId: number
  }[]
  removedDiscount: {
    propertyId: string
    discountId: number
  }[]
}

export interface ApplyDiscountByPropertyRequest
  extends Partial<AxiosRequestConfig> {
  data: ApplyDiscountByPropertyRequestData
}

export function applyDiscountByProperty(
  {
    data,
    method = "put",
    url = `${defaultUrl}/by-property`,
    ...config
  }: ApplyDiscountByPropertyRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<ApplyDiscountByPropertyResponse> {
  return axiosInstance({
    data,
    method,
    url,
    ...config,
  }) as Promise<ApplyDiscountByPropertyResponse>
}

// --------------- Ready made discount ------------------
export interface TieredDiscountListType {
  discount_id: string
  discount_type: string
  min_stay?: string
  max_stay?: string
  discount_percent: string
  days?: number
  effective_from?: string
  effective_to?: string
  expires_on?: string
  number_of_bookings?: number
  newListingUsedCount?: number
  min_discount?: number | null
  max_discount?: number | null
}

export interface DiscountObjType {
  discountTemplateName: string
  description: string
  lastMinuteDays: number
  discountType: string
  status: string
  tieredDiscountList: TieredDiscountListType[]
  isDefault?: boolean
}

export interface ReadyMadeDiscountsData {
  type: string
  title: string
  description: string
  discountDetails: string
  discountObj: DiscountObjType
  imageURL: string
}

export interface ReadyMadeDiscountsDataTypes {
  readyMadeDiscounts: ReadyMadeDiscountsData[]
}

export interface GetReadyMadeDiscountResponseData extends ResponseData {
  data: ReadyMadeDiscountsDataTypes
}

export interface GetReadyMadeDiscountResponse extends AxiosResponse {
  data: GetReadyMadeDiscountResponseData
}

export function getReadyMadeDiscount(
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<GetReadyMadeDiscountResponse> {
  return axiosInstance({
    method: "get",
    url: `${defaultUrl}/ready-made-discount`,
  }) as Promise<GetReadyMadeDiscountResponse>
}

// ---------------- new discount get Template Properties ------------------
export interface NewDiscountTemplatePropertyContentData {
  pmcId: string
  pmsUnitId: string
  propertyName: string
  activeDiscountList: string[]
  activeDiscountPropertyCount: number
  address: string
  templateId: number
  propertyId: string
}

export interface NewDiscountTemplatePropertyData {
  content: NewDiscountTemplatePropertyContentData[]
  pageable: {
    sort: {
      empty: boolean
      sorted: boolean
      unsorted: boolean
    }
    offset: number
    pageNumber: number
    pageSize: number
    paged: boolean
    unpaged: boolean
  }
  last: boolean
  totalPages: number
  totalElements: number
  number: number
  sort: {
    empty: boolean
    sorted: boolean
    unsorted: boolean
  }
  size: number
  first: boolean
  numberOfElements: number
  empty: boolean
}

export interface NewDiscountTemplatePropertyResponseData extends ResponseData {
  data: NewDiscountTemplatePropertyData
}

export interface NewDiscountTemplatePropertyResponse extends AxiosResponse {
  data: NewDiscountTemplatePropertyResponseData
}

export interface NewDiscountTemplatePropertiesRequestData {
  pmcId: string
  templateId: number | string
  keyword: string
  pageNo: number
  noOfRecords: number
  isAdminDiscount: boolean
}

export interface NewDiscountTemplatePropertiesRequest
  extends Partial<AxiosRequestConfig> {
  params: NewDiscountTemplatePropertiesRequestData
}

export function getNewDiscountTemplateProperties(
  {
    params,
    method = "post",
    url = "discount",
    ...config
  }: NewDiscountTemplatePropertiesRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<NewDiscountTemplatePropertyResponse> {
  return axiosInstance({
    data: params,
    method,
    url: `${
      process.env.REACT_APP_BASE_API_URL || ""
    }/templates/${url}/properties/by-pmcId`,
    ...config,
  }) as Promise<NewDiscountTemplatePropertyResponse>
}

// -------------- get discount calendar api ----------
export interface CalendarDateData {
  arrival: boolean
  available: boolean
  base_price: number
  bookingId: string | null
  bookingNumber: string | null
  date: number
  extended_discount: number | null
  last_min_discount: number | null
  discount: number | null
  discount_name: string | null
  discount_type: string
  max_nights: number
  min_nights: number
  noOfGuest: number | null
  pm_base_price: number | null
  rentAmount: null
  valid_from: string
  whimstay_availability: boolean
}

export interface AnnotatedCalendarDateData extends CalendarDateData {
  isBookingStartDay: boolean
  isBookingEndDay: boolean
  isModifiable: boolean
}

export interface GetCalendarData {
  time: string
  apiStatus: ApiStatus
  status?: number
  message?: string
  errorCode?: number
  errorMessage?: string
  data: CalendarDateData[]
}

export interface GetCalendarResponse extends AxiosResponse {
  data: GetCalendarData
}

export interface GetCalendarRequestData {
  pmsUnitId: string
  pmcId: string
}

export interface GetCalendarRequest extends Partial<AxiosRequestConfig> {
  params: GetCalendarRequestData
}

const defaultCalendarUrl = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/properties/calendar-with-discount`

export function getCalendarData(
  { params, method = "get", url = defaultCalendarUrl }: GetCalendarRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<GetCalendarResponse> {
  return axiosInstance({
    params,
    method,
    url,
  }) as Promise<GetCalendarResponse>
}

// --------- Get One Default Template By PMC ID Api -----------
export interface GetOneDefaultTemplateByPmcIdData {
  data: DiscountTemplateDataType
}

export interface GetOneDefaultTemplateByPmcIdResponse extends AxiosResponse {
  data: GetOneDefaultTemplateByPmcIdData
}

export interface GetOneDefaultTemplateByPmcIdRequestData {
  pmcId: string
}

export interface GetOneDefaultTemplateByPmcIdRequest
  extends Partial<AxiosRequestConfig> {
  params: GetOneDefaultTemplateByPmcIdRequestData
}

export function getOneDefaultTemplateByPmcId(
  {
    params,
    method = "get",
    url = `${defaultUrl}/default-template`,
    ...config
  }: GetOneDefaultTemplateByPmcIdRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<GetOneDefaultTemplateByPmcIdResponse> {
  return axiosInstance({
    params,
    method,
    url,
    ...config,
  }) as Promise<GetOneDefaultTemplateByPmcIdResponse>
}
